import { useCallback, useEffect, useState } from "react";
import useApi from "./useApi";
import { loadWorkflowRun, WorkflowRunLoadResult } from "../services/workflowService";
import { mapWorkflowGraphToDefinition } from "../features/workflows/workflowRunGraphMapper";
import { WorkflowDefinition } from "../features/workflows/workflowDefinitionsData";
import {
  WorkflowGraphDto,
  WorkflowInstanceDetailDto,
  WorkflowInstanceStatusDto,
  WorkflowInstanceTaskDto,
} from "../types/workflowDto";

/** One real case as the Run screen renders it. Stage ids are the canvas ids the mapper assigns. */
export interface WorkflowRunCase {
  instanceId: number;
  refNo: string;
  status: WorkflowInstanceStatusDto;
  /** The concurrency token every later write must send back. */
  rowVersionTag: string;
  /** The Portal user who started the case — the one the PIF server steps belong to. */
  startedBy: number | null;
  definition: WorkflowDefinition;
  currentStageId: string | null;
  /** Stages the case has left, from the timeline — not "everything before the current one",
   *  because a conditional can skip stages. */
  doneStageIds: string[];
  /** The stage whose approval was rejected, if any. The engine has no `rejected` status; a
   *  rejected case ends wherever its reject gate routes. */
  rejectedStageId: string | null;
  /** Saved field values keyed by canvas field id. */
  values: Record<string, string>;
  /** Carried-forward variables keyed by VarKey — e.g. the `employeeId` a web-service step
   *  captured. Masked values are left out rather than shown masked. */
  variables: Record<string, string>;
  /** The case's materialized checklist tasks, across every Task stage it has reached. */
  tasks: WorkflowInstanceTaskDto[];
  /** Set while the case sits on a Pause stage — when it resumes on its own. Eastern, as stored. */
  pausedUntil: string | null;
  /** The caller may close the case on a Cancel stage (owner or admin) - see WorkflowInstanceDetailDto. */
  canClose: boolean;
  /** The owner's display name, for "Waiting for {owner} to close this case". */
  ownerName: string | null;
  /** The latest rejected or cancelled approval - who, where and why - for "PIF not approved". */
  decline: WorkflowRunDecline | null;
  /** The stage key the case was closed (cancelled) from, so the end screen can tell an approved PIF
   *  whose employee couldn't be created apart from a rejection. Null while the case is open. */
  closedFromStageKey: string | null;
}

export interface WorkflowRunDecline {
  decision: "reject" | "cancel";
  by: string | null;
  stageName: string | null;
  reason: string | null;
}

export type WorkflowRunLoadState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; runCase: WorkflowRunCase }
  | { status: "not-found" }
  | { status: "forbidden" }
  | { status: "error" };

const isOpen = (status: WorkflowInstanceStatusDto) => status === "draft" || status === "active";

const toClosedFromStageKey = (detail: WorkflowInstanceDetailDto, graph: WorkflowGraphDto): string | null => {
  if (detail.instance.status !== "cancelled") return null;
  const closed = [...detail.timeline].reverse().find((event) => event.eventType === "cancelled");
  return graph.stages.find((stage) => stage.id === closed?.fromStageId)?.stageKey ?? null;
};

const toDecline = (approvals: WorkflowInstanceDetailDto["approvals"]): WorkflowRunDecline | null => {
  const declined = [...approvals]
    .reverse()
    .find((approval) => approval.decision === "reject" || approval.decision === "cancel");
  return declined
    ? {
        decision: declined.decision === "reject" ? "reject" : "cancel",
        by: declined.approverName ?? null,
        stageName: declined.stageName,
        reason: declined.reason?.trim() || null,
      }
    : null;
};

export const toWorkflowRunCase = (detail: WorkflowInstanceDetailDto, graph: WorkflowGraphDto): WorkflowRunCase => {
  const { instance } = detail;
  const currentStageId = instance.currentStageId === null ? null : String(instance.currentStageId);
  const leftStageIds = new Set(
    detail.timeline
      .map((event) => event.fromStageId)
      .filter((stageId): stageId is number => stageId !== null)
      .map(String),
  );
  // A case sent back to an earlier stage has left it before; while it is open it is current.
  if (currentStageId && isOpen(instance.status)) leftStageIds.delete(currentStageId);
  const rejected = [...detail.approvals].reverse().find((approval) => approval.decision === "reject");

  return {
    instanceId: instance.id,
    refNo: instance.refNo,
    status: instance.status,
    rowVersionTag: instance.rowVersionTag,
    startedBy: instance.startedBy ?? null,
    definition: mapWorkflowGraphToDefinition(graph),
    currentStageId,
    doneStageIds: [...leftStageIds],
    rejectedStageId: rejected ? String(rejected.stageId) : null,
    values: Object.fromEntries(
      detail.values
        .filter((value) => value.fieldId !== null)
        .map((value) => [String(value.fieldId), value.value ?? ""]),
    ),
    variables: Object.fromEntries(
      detail.values
        .filter((value) => value.varKey && !value.isMasked && value.value)
        .map((value) => [value.varKey as string, value.value as string]),
    ),
    tasks: detail.tasks,
    pausedUntil: instance.pausedUntil,
    canClose: Boolean(detail.canClose),
    ownerName: instance.ownerName ?? null,
    decline: toDecline(detail.approvals),
    closedFromStageKey: toClosedFromStageKey(detail, graph),
  };
};

/**
 * Loads a real case for the Run screen. Pass `null` when the screen is not showing a real case
 * (no instance in the URL) and the hook stays idle.
 *
 * Unexpected failures go through useApi, which raises the central alert; the screen then shows
 * the error card with Retry. 404 / 403 come back as their own states and raise no alert.
 */
const useWorkflowRunInstance = (instanceId: number | null) => {
  const { executeApi } = useApi();
  const [state, setState] = useState<WorkflowRunLoadState>({ status: instanceId ? "loading" : "idle" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!instanceId) {
      setState({ status: "idle" });
      return undefined;
    }
    let active = true;
    setState({ status: "loading" });
    executeApi<number, WorkflowRunLoadResult>(loadWorkflowRun, instanceId).then((result) => {
      if (!active) return;
      if (!result) setState({ status: "error" });
      else if (result.kind === "loaded")
        setState({ status: "loaded", runCase: toWorkflowRunCase(result.detail, result.graph) });
      else setState({ status: result.kind });
    });
    return () => {
      active = false;
    };
  }, [executeApi, instanceId, attempt]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  // A count, not a flag: two button-driven reloads can overlap, and the first to finish must not
  // clear the progress the other is still showing.
  const [refreshesInFlight, setRefreshesInFlight] = useState(0);

  const reload = useCallback(
    async (showProgress: boolean): Promise<boolean> => {
      if (!instanceId) return false;
      if (showProgress) setRefreshesInFlight((count) => count + 1);
      try {
        const result = await executeApi<number, WorkflowRunLoadResult>(loadWorkflowRun, instanceId);
        if (!result) return false;
        if (result.kind === "loaded")
          setState({ status: "loaded", runCase: toWorkflowRunCase(result.detail, result.graph) });
        else setState({ status: result.kind });
        return true;
      } finally {
        if (showProgress) setRefreshesInFlight((count) => count - 1);
      }
    },
    [executeApi, instanceId],
  );

  /**
   * Reloads the case after a write moved it, without dropping back to the skeleton: the current
   * render stays up until the new one is ready, and stays up if the reload fails (the alert has
   * already been raised). Resolves false only on that failure. `refreshing` is true meanwhile, so
   * the screen can show progress over the stage it is about to replace.
   */
  const refresh = useCallback(() => reload(true), [reload]);

  /**
   * The same reload with no progress shown - for background writes the user didn't ask for (pif-3's
   * server-written display names), which would otherwise flash the page's loader a third time
   * after a submit's own two (the write, then the reload).
   */
  const refreshQuietly = useCallback(() => reload(false), [reload]);

  return { state, retry, refresh, refreshQuietly, refreshing: refreshesInFlight > 0 };
};

export default useWorkflowRunInstance;
